/**
 * Gateways Semakiot lists that this deployment can work with.
 *
 * Gateways are not in the sensor hardware registry — the platform registers
 * them as coverage, not as ingestible measurement models. This catalogue is
 * reference only: listing names, SKUs and photos come from the Semakiot
 * storefront; prices stay the placeholder on that site and must not be quoted.
 */

export interface GatewayCatalogueEntry {
  id: string;
  manufacturer: string;
  model: string;
  listingName: string;
  sku: string;
  reseller: string;
  listingUrl: string;
  /** Local product photograph (Semakiot listing image, converted). */
  imageSrc: string;
  placement: "indoor" | "outdoor";
  summary: string;
  confirmed: readonly { label: string; value: string; source?: string }[];
  toVerify: readonly { topic: string; why: string; answeredBy: string }[];
}

const SEMAKIOT = "Semakiot storefront";

export const GATEWAY_CATALOGUE: readonly GatewayCatalogueEntry[] = [
  {
    id: "milesight-ug63",
    manufacturer: "Milesight",
    model: "UG63",
    listingName: "UG63-915M Mini LoRaWAN Gateway",
    sku: "MO082006",
    reseller: "Semakiot",
    listingUrl: "https://semakiot.com/productos/ug63-915m/",
    imageSrc: "/marketing/gateway-ug63.jpg",
    placement: "indoor",
    summary:
      "Compact indoor 8-channel LoRaWAN gateway. Semakiot lists the Ethernet UG63-915M. Suitable for a small site or as a fill-in radio beside a larger UG56/UG65/UG67 footprint. The ZonaClara pilot radio plan for Argentina is AU915-928 FSB 2 — confirm the ordered SKU matches that plan.",
    confirmed: [
      { label: "Manufacturer", value: "Milesight" },
      { label: "Model", value: "UG63" },
      { label: "Semakiot SKU", value: "MO082006", source: SEMAKIOT },
      { label: "Channels", value: "8 (SX1302)", source: SEMAKIOT },
      { label: "Placement", value: "Indoor", source: SEMAKIOT },
      { label: "Backhaul on this listing", value: "Ethernet", source: SEMAKIOT },
    ],
    toVerify: [
      {
        topic: "AU915 sub-band on the unit actually shipped",
        why: "The listing name says 915M; Argentina needs AU915 FSB 2 on the gateway and every end device",
        answeredBy: "Semakiot purchase order, then a field join",
      },
      {
        topic: "Pricing and lead time",
        why: "Storefront price is a placeholder. Not to be quoted",
        answeredBy: "Semakiot",
      },
    ],
  },
  {
    id: "milesight-ug56",
    manufacturer: "Milesight",
    model: "UG56",
    listingName: "UG56-915M Industrial LoRaWAN Gateway",
    sku: "MO082013",
    reseller: "Semakiot",
    listingUrl: "https://semakiot.com/productos/ug56-915m/",
    imageSrc: "/marketing/gateway-ug56.jpg",
    placement: "indoor",
    summary:
      "Industrial indoor 8-channel gateway. Semakiot lists UG56-915M with Ethernet and Wi-Fi backhaul. The usual choice when one radio should carry many sensors on a site or a shared city-block footprint.",
    confirmed: [
      { label: "Manufacturer", value: "Milesight" },
      { label: "Model", value: "UG56" },
      { label: "Semakiot SKU", value: "MO082013", source: SEMAKIOT },
      { label: "Channels", value: "8 (SX1302)", source: SEMAKIOT },
      { label: "Placement", value: "Indoor / industrial", source: SEMAKIOT },
      {
        label: "Backhaul on this listing",
        value: "Ethernet and Wi-Fi (cellular is a separate SKU)",
        source: SEMAKIOT,
      },
    ],
    toVerify: [
      {
        topic: "AU915 sub-band and antenna kit for the order",
        why: "Coverage surveys fail when the plan or antenna does not match the site",
        answeredBy: "Semakiot and a radio survey",
      },
      {
        topic: "Pricing and lead time",
        why: "Storefront price is a placeholder. Not to be quoted",
        answeredBy: "Semakiot",
      },
    ],
  },
  {
    id: "milesight-ug67",
    manufacturer: "Milesight",
    model: "UG67",
    listingName: "UG67-L08GL-915M Outdoor LoRaWAN Gateway (cellular)",
    sku: "MO082019",
    reseller: "Semakiot",
    listingUrl: "https://semakiot.com/productos/ug67-l08gl-915m/",
    imageSrc: "/marketing/gateway-ug67.jpg",
    placement: "outdoor",
    summary:
      "Outdoor IP67 8-channel gateway with cellular backhaul on the Semakiot listing (UG67-L08GL-915M). For tank yards, rural cisterns and sites without reliable indoor Ethernet. Ethernet and Wi-Fi are also described as backhaul options on the same page.",
    confirmed: [
      { label: "Manufacturer", value: "Milesight" },
      { label: "Model", value: "UG67" },
      { label: "Semakiot SKU", value: "MO082019", source: SEMAKIOT },
      { label: "Channels", value: "8 (SX1302)", source: SEMAKIOT },
      { label: "Placement", value: "Outdoor, IP67", source: SEMAKIOT },
      {
        label: "Backhaul on this listing",
        value: "Ethernet, Wi-Fi and cellular (4G)",
        source: SEMAKIOT,
      },
    ],
    toVerify: [
      {
        topic: "Whether the site needs cellular or Ethernet-only outdoor",
        why: "Semakiot also lists non-cellular outdoor variants; ordering the wrong backhaul leaves a radio that cannot reach the network server",
        answeredBy: "Site survey, then Semakiot",
      },
      {
        topic: "Pricing and lead time",
        why: "Storefront price is a placeholder. Not to be quoted",
        answeredBy: "Semakiot",
      },
    ],
  },
];

export function findGatewayCatalogueEntry(id: string): GatewayCatalogueEntry | undefined {
  return GATEWAY_CATALOGUE.find((entry) => entry.id === id);
}
